import { restApi } from '../../../api/restApiSlice';

export type DiscoverabilityStatusLevel =
  | 'no_data'
  | 'critical'
  | 'needs_improvement'
  | 'healthy';

export type DiscoverabilityStatusTrigger =
  | 'missing_crawlers'
  | 'access_blocked_pages'
  | 'access_limited_pages';

export type WarmingUpDiscoverabilityStatusDomain = {
  domain: string;
  warmingUp: true;
};

export type EvaluableDiscoverabilityStatusDomain = {
  domain: string;
  warmingUp: false;
  missingCrawlers: string[];
  accessBlockedPageCount: number;
  accessLimitedPageCount: number;
};

export type DiscoverabilityStatusDomain =
  | WarmingUpDiscoverabilityStatusDomain
  | EvaluableDiscoverabilityStatusDomain;

export function isEvaluableDomain(
  domain: DiscoverabilityStatusDomain
): domain is EvaluableDiscoverabilityStatusDomain {
  return !domain.warmingUp;
}

export type DiscoverabilityStatusResponse = {
  status: DiscoverabilityStatusLevel;
  triggers: DiscoverabilityStatusTrigger[];
  evaluatedAt: string;
  domains: DiscoverabilityStatusDomain[];
};

const NO_DATA_RESPONSE: DiscoverabilityStatusResponse = {
  status: 'no_data',
  triggers: [],
  evaluatedAt: '',
  domains: [],
};

const discoverabilityStatusApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    getDiscoverabilityStatus: builder.query<DiscoverabilityStatusResponse, void>({
      // A 404 means the daily job hasn't produced a status for this user yet.
      // Resolve it to a no_data response so callers handle a single shape.
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({ url: '/v3/ai-insights/discoverability/status', method: 'GET' });
        if (result.error) {
          if (result.error.status === 404) {
            return { data: NO_DATA_RESPONSE };
          }
          return { error: result.error };
        }
        return { data: result.data as DiscoverabilityStatusResponse };
      },
      providesTags: ['DiscoverabilityStatus'],
    }),
  }),
});

export { discoverabilityStatusApi };
export const { useGetDiscoverabilityStatusQuery } = discoverabilityStatusApi;
